import { TAppDispatch, TGetState } from 'store/types'
import slice from 'store/features/audio/slice'

import {
  deleteUsersSound_API,
  getSoundsGenres_API,
  getUserSounds_API,
  getUsedLibSounds_API,
  searchSounds_API
} from 'api'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { IUploadedAudioResponse } from 'types/userMediaUpload'
import { getQueryStringForArrays } from 'utils'
import { ApiError, ReduxThunkError } from 'utils/errors'
import { handleApiError } from 'store/utils'
import { captureException } from '@sentry/react'
import { ErrorKeys } from 'consts/errorKeys'

const { actions } = slice

const getSoundsGenres =
  () => async (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()

    const projectDuration = projectDataSelectors.duration(state)

    const query = projectDuration ? `duration=${projectDuration}` : ''

    const response = await getSoundsGenres_API(query)
    dispatch(actions.setSoundsGenres(response.data))
  }

const searchLibSounds =
  (text: string) => async (_dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const projectDuration = projectDataSelectors.duration(state)

    const params = new URLSearchParams({ text, limit: '20' })
    if (projectDuration) params.append('duration', String(projectDuration))

    const response = await searchSounds_API(params.toString())
    return response.data
  }

const getUserSounds = () => async (dispatch: TAppDispatch) => {
  try {
    const response = await getUserSounds_API()
    dispatch(actions.setUserSounds(response.data))
  } catch (error) {
    if (!(error instanceof ApiError)) {
      captureException(new ReduxThunkError(error))
    }
    dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
  }
}

const deleteUsersSound =
  (
    soundId: number,
    onDeleteComplete: () => void,
    onShowUsageWarning: (
      templateProjectIds: number[],
      videoProjectIds: number[]
    ) => void
  ) =>
  async (dispatch: TAppDispatch) => {
    try {
      const response = await deleteUsersSound_API(soundId)
      if (
        response.data.projectIds.length ||
        response.data.videoProjectIds.length
      ) {
        onShowUsageWarning(
          response.data.projectIds,
          response.data.videoProjectIds
        )
      } else {
        dispatch(actions.deleteUsersSound(soundId))
      }
    } catch (error) {
      if (!(error instanceof ApiError)) {
        captureException(new ReduxThunkError(error))
      }
      dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
    } finally {
      onDeleteComplete()
    }
  }

const unshiftUserSound =
  (uploadedAudios: IUploadedAudioResponse[]) =>
  async (dispatch: TAppDispatch) => {
    const userSounds = uploadedAudios.map(audio => ({
      createdAt: '',
      downloadPath: '',
      duration: audio.duration,
      fileSize: audio.fileSize,
      id: audio.id,
      mime: audio.mime,
      path: audio.path,
      title: audio.title,
      userId: audio.userId,
      voiceOver: audio.voiceOver
    }))

    dispatch(actions.unshiftUserSound(userSounds))
  }

const getUsedLibSounds =
  () => async (dispatch: TAppDispatch, getState: TGetState) => {
    try {
      const libAudioTitlesFromProject =
        projectDataSelectors.libAudioTitlesFromProject(getState())

      if (!libAudioTitlesFromProject.length) return

      const query = getQueryStringForArrays(
        'soundTitles',
        libAudioTitlesFromProject
      )
      const response = await getUsedLibSounds_API(query)
      dispatch(actions.setUsedLibSounds(response.data))
    } catch (error) {
      if (!(error instanceof ApiError)) {
        captureException(new ReduxThunkError(error))
      }
      dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
    }
  }

export const audioThunks = {
  getSoundsGenres,
  searchLibSounds,
  getUserSounds,
  deleteUsersSound,
  unshiftUserSound,
  getUsedLibSounds
}
